import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { Textarea } from "@/components/Textarea";
import { Select } from "@/components/Select";
import { PrioritySelect } from "@/components/PrioritySelect";
import { SearchableMultiSelect } from "@/components/SearchableMultiSelect";
import { tasksApi, authApi, developmentApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

interface NewTaskModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (task: any) => void;
    statuses: any[];
    task?: any | null;
    prefilledStatusId?: number | null;
    prefilledSprintId?: number | null;
    prefilledDevelopmentId?: number | string | null;
}

export const NewTaskModal: React.FC<NewTaskModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    statuses,
    task,
    prefilledStatusId,
    prefilledSprintId,
    prefilledDevelopmentId,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();
    const { user } = useAuth();
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [statusId, setStatusId] = useState("");
    const [priority, setPriority] = useState<number>(2);
    const [assignees, setAssignees] = useState<number[]>([]);
    const [developmentId, setDevelopmentId] = useState<string>("");
    const [relatedTasks, setRelatedTasks] = useState<number[]>([]);
    const [dependencies, setDependencies] = useState<number[]>([]);

    const [users, setUsers] = useState<any[]>([]);
    const [developments, setDevelopments] = useState<any[]>([]);
    const [allTasks, setAllTasks] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (task) {
                setTitle(task.title || "");
                setDescription(task.description || "");
                setStatusId(task.status_id ? String(task.status_id) : "");
                setPriority(
                    typeof task.priority === "number"
                        ? task.priority
                        : parseInt(task.priority, 10) || 2
                );
                setAssignees(
                    task.assignee_id
                        ? [task.assignee_id]
                        : task.assignee?.id
                          ? [task.assignee.id]
                          : []
                );
                setDevelopmentId(
                    task.development_id ? String(task.development_id) : ""
                );
                setRelatedTasks(
                    task.related_tasks?.map((t: any) => t.id) || []
                );
                setDependencies(task.dependencies?.map((t: any) => t.id) || []);
            } else {
                setTitle("");
                setDescription("");
                setPriority(2);
                if (prefilledStatusId) {
                    setStatusId(String(prefilledStatusId));
                } else {
                    const todoStatus =
                        statuses.find((s) => s.is_default) || statuses[0];
                    setStatusId(todoStatus ? String(todoStatus.id) : "");
                }
                if (user) {
                    setAssignees([user.id]);
                } else {
                    setAssignees([]);
                }
                if (prefilledDevelopmentId) {
                    setDevelopmentId(String(prefilledDevelopmentId));
                } else {
                    setDevelopmentId("");
                }
                setRelatedTasks([]);
                setDependencies([]);
            }

            authApi
                .users()
                .then((res) => {
                    if (res.result && Array.isArray(res.data)) {
                        setUsers(res.data);
                    }
                })
                .catch((err) =>
                    console.error("Error loading users for assignees", err)
                );

            developmentApi
                .list()
                .then((res) => {
                    if (res.result && Array.isArray(res.data)) {
                        setDevelopments(res.data);
                    }
                })
                .catch((err) =>
                    console.error("Error loading developments", err)
                );

            tasksApi
                .list()
                .then((res) => {
                    if (res.result && Array.isArray(res.data)) {
                        setAllTasks(res.data);
                    }
                })
                .catch((err) => console.error("Error loading tasks", err));
        }
    }, [isOpen, task, statuses, prefilledStatusId, user]);

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (!title.trim() || !statusId) {
            showError(t("tasks.new_modal.error_mandatory"));
            return;
        }

        setLoading(true);
        try {
            if (task && task.id) {
                const res = await tasksApi.update(task.id, {
                    title,
                    description,
                    status_id: parseInt(statusId),
                    priority,
                    assignee_id: assignees.length > 0 ? assignees[0] : null,
                    development_id: developmentId
                        ? parseInt(developmentId)
                        : null,
                    related_tasks: relatedTasks,
                    dependencies: dependencies,
                });

                if (res.result) {
                    showSuccess(t("tasks.updated"));
                    onSuccess(res.data);
                    onClose();
                } else {
                    showError(res.message || t("errors.default"));
                }
            } else {
                const res = await tasksApi.create({
                    title,
                    description,
                    status_id: parseInt(statusId),
                    priority,
                    sprint_id: prefilledSprintId || null,
                    assignee_id: assignees.length > 0 ? assignees[0] : null,
                    development_id: developmentId
                        ? parseInt(developmentId)
                        : null,
                    related_tasks: relatedTasks,
                    dependencies: dependencies,
                });

                if (res.result) {
                    showSuccess(t("tasks.new_modal.success"));
                    onSuccess(res.data);
                    onClose();
                } else {
                    showError(res.message || t("tasks.new_modal.error_create"));
                }
            }
        } catch (err) {
            console.error("Error al guardar tarea:", err);
            showError(t("tasks.new_modal.error_connection"));
        } finally {
            setLoading(false);
        }
    };

    const statusOptions = statuses.map((s) => ({
        value: String(s.id),
        label: t(`tasks.statuses.${s.tag}`) || s.name,
    }));

    const assigneeOptions = useMemo(() => {
        return users
            .filter(
                (u) =>
                    u.roles?.includes("it") || u.roles?.includes("development")
            )
            .map((u) => ({
                id: u.id,
                name: u.name,
                subtitle: u.email,
            }));
    }, [users]);

    const developmentOptions = useMemo(() => {
        return developments.map((d) => ({
            value: String(d.id),
            label: `${d.code} - ${d.title}`,
        }));
    }, [developments]);

    const taskOptions = useMemo(() => {
        return allTasks
            .filter((t) => !task || t.id !== task.id)
            .map((t) => ({
                id: t.id,
                name: `${t.code} - ${t.title}`,
            }));
    }, [allTasks, task]);

    const modalTitle = task
        ? `${task.code ? `${task.code} - ` : ""}${t("tasks.edit_title")}`
        : t("tasks.new_modal.title");

    const submitLabel = task ? t("common.save") : t("tasks.new_modal.submit");

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={modalTitle}
            onSubmit={handleSubmit}
            submitLabel={submitLabel}
            cancelLabel={t("tasks.new_modal.cancel")}
            isSubmitting={loading}
            submitDisabled={!title.trim() || !statusId}
            maxWidth="xl"
        >
            <div className="space-y-4">
                <Input
                    label={t("tasks.new_modal.title_label")}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t("tasks.new_modal.title_placeholder")}
                    required
                />

                <Textarea
                    label={t("tasks.new_modal.description")}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t("tasks.new_modal.description_placeholder")}
                    rows={8}
                />

                <div>
                    <label className="block text-xs font-bold text-slate-700 dark:text-zinc-300 mb-1.5">
                        {t("tasks.new_modal.status")}
                    </label>
                    <Select
                        options={statusOptions}
                        value={statusId}
                        onChange={setStatusId}
                        full
                    />
                </div>

                <div>
                    <PrioritySelect
                        value={priority}
                        onChange={setPriority}
                        label={t("tasks.new_modal.priority")}
                        full
                    />
                </div>

                <SearchableMultiSelect
                    label={t("tasks.new_modal.assignees_label")}
                    value={assignees}
                    onChange={setAssignees}
                    options={assigneeOptions}
                    placeholder={t("tasks.new_modal.assignees_placeholder")}
                    singleSelect={true}
                />

                <SearchableMultiSelect
                    label={t("tasks.new_modal.development")}
                    value={developmentId ? [parseInt(developmentId)] : []}
                    onChange={(val: number[]) =>
                        setDevelopmentId(val.length > 0 ? String(val[0]) : "")
                    }
                    options={developmentOptions.map((d) => ({
                        id: parseInt(d.value),
                        name: d.label,
                    }))}
                    placeholder={
                        t("tasks.new_modal.development_placeholder") ||
                        "Seleccionar desarrollo"
                    }
                    singleSelect={true}
                />

                <SearchableMultiSelect
                    label={
                        t("tasks.new_modal.related_tasks") ||
                        "Tareas relacionadas"
                    }
                    value={relatedTasks}
                    onChange={setRelatedTasks}
                    options={taskOptions}
                    placeholder={
                        t("tasks.new_modal.related_tasks_placeholder") ||
                        "Buscar tareas"
                    }
                />

                <SearchableMultiSelect
                    label={t("tasks.new_modal.dependencies") || "Dependencias"}
                    value={dependencies}
                    onChange={setDependencies}
                    options={taskOptions}
                    placeholder={
                        t("tasks.new_modal.dependencies_placeholder") ||
                        "Buscar tareas bloqueantes"
                    }
                />
            </div>
        </Modal>
    );
};
